import React from "react";
import { Loader2, TriangleAlert, RotateCcw } from "lucide-react";
import { apexStatus } from "./apexReport";

const barColor = (score) => (score >= 70 ? "bg-green-500" : score >= 40 ? "bg-amber-400" : "bg-red-500");
const textColor = (score) =>
  score >= 70 ? "text-green-600 dark:text-green-400" : score >= 40 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";

/** Small chip for the problem header row: score, "Reviewing…" or "Review failed". */
export const ApexScoreChip = ({ submission, isDark }) => {
  const status = apexStatus(submission);
  if (status === "completed") {
    const score = Math.round(submission.coding_score ?? 0);
    const cls =
      score >= 70
        ? isDark ? "bg-green-400/10 text-green-400 border-green-400/30" : "bg-green-50 text-green-600 border-green-200"
        : score >= 40
        ? isDark ? "bg-amber-400/10 text-amber-400 border-amber-400/30" : "bg-amber-50 text-amber-600 border-amber-200"
        : isDark ? "bg-red-400/10 text-red-400 border-red-400/30" : "bg-red-50 text-red-600 border-red-200";
    return <span className={`text-sm px-3 py-1 rounded-full font-semibold border ${cls}`}>{score}/100</span>;
  }
  if (status === "failed") {
    return (
      <span className={`text-sm px-3 py-1 rounded-full font-medium border ${isDark ? "bg-red-400/10 text-red-300 border-red-400/30" : "bg-red-50 text-red-600 border-red-200"}`}>
        Review failed
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full font-medium border ${isDark ? "bg-blue-400/10 text-blue-300 border-blue-400/30" : "bg-blue-50 text-blue-600 border-blue-200"}`}>
      <Loader2 className="w-3 h-3 animate-spin" />Reviewing
    </span>
  );
};

/** Expanded-card body for an Apex answer: rubric breakdown + overall remark, or its pending/failed state. */
const ApexRubricResult = ({ submission, isDark, canRetry, retrying, onRetry }) => {
  const status = apexStatus(submission);

  if (status === "pending") {
    return (
      <div className={`flex items-center gap-2.5 py-3 px-4 rounded-xl border ${isDark ? "bg-blue-400/5 border-blue-400/20" : "bg-blue-50 border-blue-100"}`}>
        <Loader2 className="w-4 h-4 animate-spin text-blue-500 flex-shrink-0" />
        <span className="text-sm text-blue-700 dark:text-blue-300">AI review in progress. This page updates automatically.</span>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className={`flex items-center justify-between gap-3 py-3 px-4 rounded-xl border ${isDark ? "bg-red-400/5 border-red-400/20" : "bg-red-50 border-red-100"}`}>
        <span className="flex items-center gap-2.5 text-sm text-red-700 dark:text-red-300">
          <TriangleAlert className="w-4 h-4 flex-shrink-0" />The AI review could not be completed for this answer.
        </span>
        {canRetry && (
          <button
            onClick={onRetry}
            disabled={retrying}
            className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border border-red-300 text-red-700 dark:text-red-300 dark:border-red-400/40 hover:bg-red-100 dark:hover:bg-red-400/10 disabled:opacity-50 transition-colors"
          >
            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Retry review
          </button>
        )}
      </div>
    );
  }

  const rubric = Array.isArray(submission.rubric_scores) ? submission.rubric_scores : [];
  const overall = Math.round(submission.coding_score ?? 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-textSecondary dark:text-[#C9A3FF]">AI Rubric Review</p>
        <span className={`text-sm font-bold ${textColor(overall)}`}>{overall}/100</span>
      </div>

      {submission.overall_remark && (
        <p className={`text-sm leading-relaxed rounded-xl px-4 py-3 border ${isDark ? "bg-white/5 border-gray-700/40 text-gray-300" : "bg-gray-50 border-gray-100 text-gray-700"}`}>
          {submission.overall_remark}
        </p>
      )}

      {rubric.length > 0 && (
        <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700/40" : "border-gray-200"}`}>
          {rubric.map((c) => (
            <div key={c.id || c.name} className={`px-4 py-3 border-b last:border-b-0 ${isDark ? "border-gray-700/30" : "border-gray-100"}`}>
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {c.name} <span className="font-normal text-xs text-gray-400 dark:text-gray-500">· weight {c.weight}%</span>
                </p>
                <span className={`text-sm font-bold flex-shrink-0 ${textColor(c.score)}`}>{Math.round(c.score)}</span>
              </div>
              <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-gray-700" : "bg-gray-200"}`}>
                <div className={`h-full rounded-full ${barColor(c.score)}`} style={{ width: `${Math.max(0, Math.min(100, c.score))}%` }} />
              </div>
              {c.justification && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 leading-relaxed">{c.justification}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApexRubricResult;
